import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, UserCircle, Mail, Phone } from "lucide-react";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";

export default function CompaniesAdmin() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Filtros simples
  const [nameFilter, setNameFilter] = useState("");
  const [taxFilter, setTaxFilter] = useState("");

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setErr(null);
      const res = await api.get(endpoints.companies.list);
      const rows = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);

      // Normaliza por si viene _count o arrays users/padlocks
      const normalized = rows.map((c) => ({
        id: c.id,
        name: c.name ?? "",
        taxId: c.taxId ?? "",
        contact: c.contact ?? null, // { id, name, email, phone } | null
        usersCount:
          c?._count?.users ??
          (Array.isArray(c.users) ? c.users.length : null),
        padlocksCount:
          c?._count?.padlocks ??
          (Array.isArray(c.padlocks) ? c.padlocks.length : null),
      }));

      setCompanies(normalized);
    } catch (e) {
      console.error(e);
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return companies.filter((c) => {
      const okName =
        nameFilter.trim() === "" ||
        c.name.toLowerCase().includes(nameFilter.trim().toLowerCase());
      const okTax =
        taxFilter.trim() === "" ||
        (c.taxId || "").toLowerCase().includes(taxFilter.trim().toLowerCase());
      return okName && okTax;
    });
  }, [companies, nameFilter, taxFilter]);

  const clearFilters = () => {
    setNameFilter("");
    setTaxFilter("");
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Building2 size={22} /> Compañías
        </h1>
        <button
          onClick={() => navigate("/companies/create")}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          Crear Compañía
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded shadow space-y-4">
        <h2 className="text-lg font-medium">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block font-medium">Nombre</label>
            <input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Buscar por nombre"
              className="border rounded w-full p-2"
            />
          </div>
          <div>
            <label className="block font-medium">Tax ID</label>
            <input
              type="text"
              value={taxFilter}
              onChange={(e) => setTaxFilter(e.target.value)}
              placeholder="Buscar por Tax ID"
              className="border rounded w-full p-2"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-3 py-2 rounded border w-full md:w-auto"
              type="button"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {filtered.length} de {companies.length} resultados
        </div>
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            Error al cargar: {String(err?.message || err)}
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-left">Tax ID</th>
              <th className="px-4 py-2 text-left">Main Contact</th>
              <th className="px-4 py-2 text-left">Contact Email</th>
              <th className="px-4 py-2 text-left">Contact Phone</th>
              <th className="px-4 py-2 text-left">Users</th>
              <th className="px-4 py-2 text-left">Padlocks</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              filtered.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2">{c.taxId}</td>
                  <td className="px-4 py-2 flex items-center gap-2">
                    <UserCircle size={16} className="text-gray-500" />
                    {c.contact?.name || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2 flex items-center gap-2">
                    <Mail size={16} className="text-blue-600" />
                    {c.contact?.email || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2 flex items-center gap-2">
                    <Phone size={16} className="text-emerald-600" />
                    {c.contact?.phone || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    {typeof c.usersCount === "number" ? c.usersCount : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    {typeof c.padlocksCount === "number" ? c.padlocksCount : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/companies/${c.id}`)}
                        className="px-3 py-1 rounded border"
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => navigate(`/companies/${c.id}/edit`)}
                        className="px-3 py-1 rounded border"
                      >
                        Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                  No hay compañías que coincidan con los filtros.
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                  Cargando compañías...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}